import React, { createContext, useContext, useState, ReactNode } from "react";

export type BannerType = "success" | "info" | "warn" | "error";

export interface BannerItem {
  id: string;
  message: string;
  type: BannerType;
}

interface BannerContextProps {
  showBanner: (message: string, type: BannerType) => void;
  /** The live queue, newest first. Read by the host that renders it; callers only need showBanner. */
  banners: BannerItem[];
  removeBanner: (id: string) => void;
}

const BannerContext = createContext<BannerContextProps | undefined>(undefined);

const MAX_BANNERS = 3;
const BANNER_DURATION = 4000; // 4 seconds

export const BannerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<BannerItem[]>([]);

  const showBanner = (message: string, type: BannerType) => {
    const newBanner: BannerItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      message,
      type,
    };

    setBanners((prevBanners) => {
      // Add new banner at the beginning of the array
      const updatedBanners = [newBanner, ...prevBanners].slice(0, MAX_BANNERS);
      return updatedBanners;
    });

    // Auto remove the banner after duration
    setTimeout(() => {
      setBanners((prevBanners) => prevBanners.filter((banner) => banner.id !== newBanner.id));
    }, BANNER_DURATION);
  };

  const removeBanner = (id: string) => {
    setBanners((prevBanners) => prevBanners.filter((banner) => banner.id !== id));
  };

  return <BannerContext.Provider value={{ showBanner, banners, removeBanner }}>{children}</BannerContext.Provider>;
};

export const useBanner = (): BannerContextProps => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error("useBanner must be used within a BannerProvider");
  }
  return context;
};
